import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { Form, InputField } from '@epoch-ui/components/Form';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';

import {
  targetAttendeesModalSchema,
  type TargetAttendeesModalFormValues
} from './targetAttendeesModal.schema';

export interface TargetAttendeesModalProps extends React.ComponentProps<typeof Modal> {
  initialValues: Partial<TargetAttendeesModalFormValues>;
  onSubmit: (values: TargetAttendeesModalFormValues) => void | Promise<void>;
}

const TargetAttendeesModal: React.FC<TargetAttendeesModalProps> = ({
  onOpenChange,
  initialValues,
  onSubmit,
  ...props
}) => {
  const form = useForm<TargetAttendeesModalFormValues>({
    resolver: zodResolver(targetAttendeesModalSchema),
    defaultValues: initialValues,
    mode: 'onChange'
  });
  const {
    formState: { isValid, isDirty },
    reset
  } = form;

  const handleOpenChange = (open: boolean): void => {
    // Reset the form when the modal is closed
    if (!open) {
      reset(initialValues);
    }

    onOpenChange?.(open);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.stopPropagation();

    form.handleSubmit(async (data) => {
      await onSubmit({
        targetAttendance: data.targetAttendance
      });

      reset({ targetAttendance: data.targetAttendance });
    })(e);
  };

  const handleRemoveTarget = async (): Promise<void> => {
    await onSubmit({ targetAttendance: null });
    reset({ targetAttendance: null });
  };

  return (
    <Modal {...props} onOpenChange={handleOpenChange}>
      <Form {...form}>
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <ModalHeader className="tw-mb-0 tw-flex tw-justify-between">
              <ModalTitle className="tw-text-[24px] tw-font-medium">Target attendees</ModalTitle>
            </ModalHeader>
            <ModalDescription className="tw-mt-2 tw-text-sm tw-text-muted-foreground">
              Guests won&apos;t see this - it&apos;s used to calculate the attendance rate by
              comparing it to actual turnout.{' '}
              <a
                href="https://help.epochapp.com/articles/what-is-target-attendance"
                target="_blank"
                rel="noreferrer"
                className="tw-text-primary-grey tw-underline"
              >
                Learn more
              </a>
            </ModalDescription>

            <InputField
              name="targetAttendance"
              label="Target attendees"
              type="number"
              className="tw-my-5"
              inputClassName="tw-h-11 tw-w-full"
              min="1"
            />

            <ModalFooter className="tw-mt-0">
              {initialValues.targetAttendance ? (
                <div className="tw-grid tw-w-full tw-grid-cols-2 tw-gap-x-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="tw-w-full tw-rounded-lg"
                    onClick={handleRemoveTarget}
                  >
                    Remove target
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    className="tw-w-full tw-rounded-lg"
                    disabled={!isValid || !isDirty}
                  >
                    Update target
                  </Button>
                </div>
              ) : (
                <Button
                  type="submit"
                  size="sm"
                  className="tw-w-full tw-rounded-lg"
                  disabled={!isValid || !isDirty}
                >
                  Set target
                </Button>
              )}
            </ModalFooter>
          </form>
        </ModalContent>
      </Form>
    </Modal>
  );
};

export { TargetAttendeesModal };
